import React, { useCallback, useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------
 * Corredor AI — native chat widget (replaces Voiceflow)
 *
 *  • Floating orange pill bottom-right on every page
 *  • Black chat panel with Bebas Neue header
 *  • SSE streaming from POST /api/corredor/chat
 *  • Multi-turn history persisted server-side per session_id
 * ------------------------------------------------------------ */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "cd_corredor_session_id";

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid =
        "crd_" +
        Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36);
      localStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch (_) {
    return "crd_anon_" + Date.now().toString(36);
  }
}

const GREETING = {
  role: "assistant",
  content:
    "Hi — I'm Corredor AI, the procurement intelligence engine for the Chihuahua–El Paso corridor. Are you sourcing manufacturing, listing your plant, or looking at this as an investor?",
};

export default function CorredorChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const sessionId = useRef(getOrCreateSessionId());
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore history on first open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/corredor/session/${sessionId.current}`,
        );
        if (!r.ok) return;
        const data = await r.json();
        const msgs = (data?.messages || []).filter(
          (m) => m.role === "user" || m.role === "assistant",
        );
        if (msgs.length) setMessages(msgs);
      } catch (_) {
        /* keep greeting */
      }
    })();
    // Only on first open per mount — sessionId.current is stable
  }, [open]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // Auto-focus on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  const send = useCallback(
    async (text) => {
      const clean = (text || "").trim();
      if (!clean || streaming) return;
      setError("");
      setInput("");

      // Optimistically push the user message + an empty assistant placeholder
      setMessages((prev) => [
        ...prev,
        { role: "user", content: clean },
        { role: "assistant", content: "" },
      ]);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const resp = await fetch(`${API_BASE}/api/corredor/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId.current,
            message: clean,
            page_url:
              typeof window !== "undefined" ? window.location.href : null,
            page_referrer:
              typeof document !== "undefined" ? document.referrer : null,
          }),
          signal: controller.signal,
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // SSE frames are separated by blank lines
          const frames = buffer.split("\n\n");
          buffer = frames.pop() || "";
          for (const frame of frames) {
            const line = frame.trim();
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            try {
              const obj = JSON.parse(payload);
              if (obj.delta) {
                setMessages((prev) => {
                  const next = prev.slice();
                  const last = next[next.length - 1];
                  if (last && last.role === "assistant") {
                    next[next.length - 1] = {
                      ...last,
                      content: (last.content || "") + obj.delta,
                    };
                  }
                  return next;
                });
              } else if (obj.error) {
                setError(obj.error);
              }
            } catch (_) {
              /* ignore malformed frame */
            }
          }
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setError("Connection error. Please try again.");
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [streaming],
  );

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return (
    <>
      {/* Floating launcher (always visible) */}
      <button
        type="button"
        data-testid="corredor-ai-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open Corredor AI chat"
        style={launcherStyle(open)}
      >
        <span aria-hidden="true" style={dotStyle} />
        Corredor AI
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="corredor-ai-panel"
          style={panelStyle}
          role="dialog"
          aria-label="Corredor AI chat"
        >
          {/* Header */}
          <div style={headerStyle}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                data-testid="corredor-ai-title"
                style={{
                  fontFamily:
                    "'Bebas Neue', 'Archivo Black', system-ui, sans-serif",
                  fontSize: 22,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  color: "#fff",
                }}
              >
                Corredor AI — Procurement Intelligence
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "rgba(255,255,255,0.65)",
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  letterSpacing: "0.02em",
                }}
              >
                Powered by Corridor Digital.
              </div>
            </div>
            <button
              type="button"
              data-testid="corredor-ai-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={closeBtnStyle}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="corredor-ai-messages"
            style={messagesStyle}
          >
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {streaming &&
              messages.length > 0 &&
              messages[messages.length - 1].role === "assistant" &&
              !messages[messages.length - 1].content && (
                <div style={typingStyle} data-testid="corredor-ai-typing">
                  <span style={dotPulse} />
                  <span style={{ ...dotPulse, animationDelay: "150ms" }} />
                  <span style={{ ...dotPulse, animationDelay: "300ms" }} />
                </div>
              )}
            {error && (
              <div data-testid="corredor-ai-error" style={errorStyle}>
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={onSubmit}
            style={composerStyle}
            data-testid="corredor-ai-composer"
          >
            <textarea
              ref={inputRef}
              data-testid="corredor-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                streaming
                  ? "Corredor AI is thinking…"
                  : "Type your message…"
              }
              disabled={streaming}
              rows={1}
              style={textareaStyle}
            />
            <button
              type="submit"
              data-testid="corredor-ai-send"
              disabled={streaming || !input.trim()}
              style={sendBtnStyle(streaming || !input.trim())}
              aria-label="Send message"
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* Inline keyframes for the typing dots — keeps the component
          self-contained without touching the global stylesheet. */}
      <style>{`
        @keyframes cdCorredorPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
}

/* ---------- bubbles ---------- */

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      data-testid={
        isUser ? "corredor-ai-msg-user" : "corredor-ai-msg-assistant"
      }
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: 10,
      }}
    >
      <div
        style={{
          maxWidth: "82%",
          background: isUser ? "#FF6B00" : "#1a1a1a",
          color: "#fff",
          padding: "9px 12px",
          fontSize: 13.5,
          lineHeight: 1.5,
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          border: isUser ? "1px solid #FF6B00" : "1px solid #2a2a2a",
        }}
      >
        {content || (isUser ? "" : "…")}
      </div>
    </div>
  );
}

/* ---------- inline styles ---------- */

function launcherStyle(open) {
  return {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 2147483646,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 18px",
    background: "#FF6B00",
    color: "#fff",
    border: "1px solid #FF6B00",
    boxShadow: "0 6px 24px rgba(255,107,0,0.35)",
    cursor: "pointer",
    fontFamily: "'Bebas Neue', 'Archivo Black', system-ui, sans-serif",
    fontSize: 16,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    transform: open ? "scale(0.96)" : "scale(1)",
    transition: "transform 120ms ease, box-shadow 120ms ease",
  };
}

const dotStyle = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#fff",
  display: "inline-block",
  boxShadow: "0 0 8px rgba(255,255,255,0.9)",
};

const panelStyle = {
  position: "fixed",
  right: 16,
  bottom: 84,
  width: "min(380px, calc(100vw - 32px))",
  height: "min(600px, calc(100vh - 120px))",
  background: "#000",
  border: "1px solid #2a2a2a",
  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 2147483647,
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "14px 16px",
  background: "#FF6B00",
  borderBottom: "1px solid #2a2a2a",
};

const closeBtnStyle = {
  background: "transparent",
  border: 0,
  color: "#fff",
  fontSize: 26,
  lineHeight: 1,
  cursor: "pointer",
  padding: "0 4px",
  fontFamily: "system-ui, sans-serif",
};

const messagesStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "14px 14px 6px",
  background: "#000",
};

const typingStyle = {
  display: "inline-flex",
  gap: 4,
  alignItems: "center",
  padding: "6px 10px",
  marginBottom: 10,
};

const dotPulse = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "#FF6B00",
  display: "inline-block",
  animation: "cdCorredorPulse 1s infinite ease-in-out",
};

const errorStyle = {
  fontSize: 12,
  color: "#ff6b6b",
  padding: "6px 4px",
  fontFamily: "'IBM Plex Mono', monospace",
};

const composerStyle = {
  display: "flex",
  gap: 8,
  padding: 10,
  borderTop: "1px solid #2a2a2a",
  background: "#0a0a0a",
};

const textareaStyle = {
  flex: 1,
  background: "#000",
  border: "1px solid #2a2a2a",
  color: "#fff",
  padding: "10px 12px",
  fontSize: 13.5,
  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  resize: "none",
  outline: "none",
  maxHeight: 120,
};

function sendBtnStyle(disabled) {
  return {
    background: disabled ? "#3a1a00" : "#FF6B00",
    color: "#fff",
    border: 0,
    padding: "0 14px",
    fontFamily: "'Bebas Neue', 'Archivo Black', system-ui, sans-serif",
    fontSize: 14,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  };
}
